/**
 * Qué ítem del menú corresponde a la pantalla en la que se está.
 *
 * Hasta ahora el menú llevaba `defaultSelectedKeys={["1"]}` y nada más: «Inicio»
 * quedaba marcado para siempre, se estuviera donde se estuviera. Se toleraba
 * con la barra siempre abierta; con la barra oculta no, porque se abre
 * justamente cuando uno no sabe dónde está.
 *
 * **Las rutas se leen del propio menú**, del `to` de cada `<Link>`, y no de una
 * segunda tabla. Una tabla paralela se desincroniza el día que alguien agrega
 * una pantalla y toca sólo un archivo --y el síntoma sería un menú que marca la
 * opción equivocada, que es peor que no marcar ninguna--.
 */

/**
 * El destino de un ítem, si su etiqueta lleva un enlace.
 *
 * Busca en profundidad y no sólo en `label.props.to`: desde que «Pendientes de
 * sincronizar» enseña el número al lado, su etiqueta es un fragmento con el
 * enlace **dentro**. Mirando sólo el primer nivel, ese ítem dejaba de
 * resolverse y era justo el que perdía la marca al entrar en él.
 */
export const destinoDe = (item) => {
  const buscar = (nodo, profundidad = 0) => {
    if (!nodo || profundidad > 6) return null;
    if (Array.isArray(nodo)) {
      for (const hijo of nodo) {
        const encontrado = buscar(hijo, profundidad + 1);
        if (encontrado) return encontrado;
      }
      return null;
    }
    if (typeof nodo.props?.to === "string") return nodo.props.to;
    return buscar(nodo.props?.children, profundidad + 1);
  };

  return buscar(item?.label);
};

/** El texto visible de un ítem: lo que se escribe para encontrarlo. */
export const tituloDe = (item) => {
  const buscar = (nodo, profundidad = 0) => {
    if (nodo == null || profundidad > 6) return null;
    if (typeof nodo === "string") return nodo.trim() || null;
    if (Array.isArray(nodo)) {
      for (const hijo of nodo) {
        const encontrado = buscar(hijo, profundidad + 1);
        if (encontrado) return encontrado;
      }
      return null;
    }
    return buscar(nodo.props?.children, profundidad + 1);
  };

  return buscar(item?.label);
};

/**
 * Si `ruta` cae dentro de `destino`, respetando los cortes de segmento.
 *
 * El corte importa: `/facturas-pendientes` empieza por `/facturas` y son dos
 * pantallas distintas. Sin esta comprobación, estar en pendientes marcaría
 * Facturas.
 */
const caeDentro = (ruta, destino) => {
  if (destino === "/") return ruta === "/";
  return ruta === destino || ruta.startsWith(`${destino}/`);
};

/**
 * @param {Array} items Los ítems del menú, tal como los devuelve `getMenuItems`.
 * @param {string} ruta El `pathname` actual, sin la parte de la consulta.
 * @returns {{seleccionadas: string[], abiertas: string[]}} La clave del ítem que
 *   toca marcar y las de los submenús que hay que desplegar para verlo. Las dos
 *   listas van vacías cuando la pantalla no está en el menú --Configuración, Mi
 *   perfil y las notas de la versión se abren desde el menú del usuario--, y eso
 *   es lo correcto: marcar algo sería mentir.
 */
export const clavesDeLaRuta = (items = [], ruta = "/") => {
  let mejor = null;

  const recorrer = (lista, padres) => {
    (lista || []).forEach((item) => {
      if (!item) return;
      const destino = destinoDe(item);
      if (destino && caeDentro(ruta, destino)) {
        // Gana el destino más largo: entre `/facturas` y `/facturas/algo`, el
        // segundo describe mejor dónde se está.
        if (!mejor || destino.length > mejor.destino.length) {
          mejor = { clave: String(item.key), destino, padres };
        }
      }
      if (item.children) recorrer(item.children, [...padres, String(item.key)]);
    });
  };

  recorrer(items, []);

  return mejor ? { seleccionadas: [mejor.clave], abiertas: mejor.padres } : { seleccionadas: [], abiertas: [] };
};
